'use client';

import { MutationFunction, QueryClient, QueryFunction, useMutation, useQuery } from '@tanstack/react-query';

import { ActionResponse, getResponseData } from '@/types/response-types';

type UseServerActionQueryOptions<
    TQueryFnData = unknown,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[],
> = Omit<Parameters<typeof useQuery<TQueryFnData, Error, TData, TQueryKey>>[0], 'queryFn'> & {
    queryFn: QueryFunction<ActionResponse<TQueryFnData>, TQueryKey>;
};

export const useServerActionQuery = <
    TQueryFnData = unknown,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[],
>(
    { queryFn, ...useQueryOpts }: UseServerActionQueryOptions<TQueryFnData, TData, TQueryKey>,
    queryClient?: QueryClient
) =>
    useQuery(
        {
            ...useQueryOpts,
            queryFn: async (context) => getResponseData(await queryFn(context)),
        },
        queryClient
    );

type UseServerActionMutationOptions<TData = unknown, TVariables = void, TOnMutateResult = unknown> = Omit<
    Parameters<typeof useMutation<TData, Error, TVariables, TOnMutateResult>>[0],
    'mutationFn'
> & {
    mutationFn: MutationFunction<ActionResponse<TData>, TVariables>;
};

export const useServerActionMutation = <TData = unknown, TVariables = void, TOnMutateResult = unknown>({
    mutationFn,
    ...useMutationOpts
}: UseServerActionMutationOptions<TData, TVariables, TOnMutateResult>) =>
    useMutation({
        ...useMutationOpts,
        mutationFn: async (variables, context) => getResponseData(await mutationFn(variables, context)),
    });
